import { useAuth0 } from '@auth0/auth0-react';
import SearchIcon from '@mui/icons-material/Search';
import { Checkbox, FormControlLabel } from '@mui/material';
import TextField from '@mui/material/TextField';

import {
  FetchAutocomplete,
  FetchAutocompleteChangeReason,
  domainCodeAtom,
} from '@australiangreens/ag-internal-components';
import { AccordionField } from '@components/DrawerAccordion';
import { useAtomValue } from 'jotai';
import { getElectionsContaining } from '@/apis/elections';
import {
  AutocompleteResult,
  ElectionRecordResult,
  TASK_CHOICE_MAPPING,
  TASK_CHOICE_MAPPING_REVERSE,
  TASK_KIND_MAPPING,
  TASK_KIND_MAPPING_REVERSE,
  TaskChoice,
  TaskKind,
  arePermissibleTaskChoicesAndKinds,
} from '@/types';
import { labelSorting } from '@/util/labelSorting';

// For this constant, the level of intetests are single characters
// when used to get data from civi. So we might as well set the ids
// to their Unicode codepoints (except for 'Unknown', which we set to zero).

export const TASK_CHOICE_MAPPING_WITH_IDS = [
  {
    id: 'Y'.codePointAt(0)!,
    label: TASK_CHOICE_MAPPING.Y,
  },
  {
    id: 'E'.codePointAt(0)!,
    label: TASK_CHOICE_MAPPING.E,
  },
  {
    id: 'N'.codePointAt(0)!,
    label: TASK_CHOICE_MAPPING.N,
  },
  {
    id: 'L'.codePointAt(0)!,
    label: TASK_CHOICE_MAPPING.L,
  },
  {
    id: 'T'.codePointAt(0)!,
    label: TASK_CHOICE_MAPPING.T,
  },
  {
    id: 'U'.codePointAt(0)!,
    label: TASK_CHOICE_MAPPING.U,
  },
  {
    id: 'I'.codePointAt(0)!,
    label: TASK_CHOICE_MAPPING.I,
  },
  {
    id: 'D'.codePointAt(0)!,
    label: TASK_CHOICE_MAPPING.D,
  },
  {
    id: 'R'.codePointAt(0)!,
    label: TASK_CHOICE_MAPPING.R,
  },
  {
    id: 0,
    label: TASK_CHOICE_MAPPING[''],
  },
];

export const TASK_KIND_MAPPING_WITH_IDS = [
  {
    id: 3,
    label: TASK_KIND_MAPPING.LB,
  },
  {
    id: 1,
    label: TASK_KIND_MAPPING.HTV,
  },
  {
    id: 2,
    label: TASK_KIND_MAPPING.PP,
  },
  {
    id: 16,
    label: TASK_KIND_MAPPING.CAP,
  },
  {
    id: 10,
    label: TASK_KIND_MAPPING.SCRUT,
  },
  {
    id: 4,
    label: TASK_KIND_MAPPING.FP,
  },
  {
    id: 6,
    label: TASK_KIND_MAPPING.DN,
  },
  {
    id: 15,
    label: TASK_KIND_MAPPING.PHS,
  },
  {
    id: 14,
    label: TASK_KIND_MAPPING.PHV,
  },
  {
    id: 9,
    label: TASK_KIND_MAPPING.PH,
  },
  {
    id: 13,
    label: TASK_KIND_MAPPING.DE,
  },
  {
    id: 12,
    label: TASK_KIND_MAPPING.TXT,
  },
  {
    id: 7,
    label: TASK_KIND_MAPPING.STALL,
  },
  {
    id: 8,
    label: TASK_KIND_MAPPING.OFC,
  },
  {
    id: 5,
    label: TASK_KIND_MAPPING.EP,
  },
  {
    id: 11,
    label: TASK_KIND_MAPPING.NO,
  },
];

const TASK_NOTE_MAX_LENGTH = 120;

type TasksFormProps = {
  onChangeElection: (
    newValue: ElectionRecordResult[],
    reason: FetchAutocompleteChangeReason,
    event: React.SyntheticEvent<Element, Event>
  ) => void;
  electionValue: ElectionRecordResult[];
  isSinceSpecificElection: boolean;
  handleSpecificElectionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeTaskChoice: (
    newValue: AutocompleteResult[],
    reason: FetchAutocompleteChangeReason,
    event: React.SyntheticEvent<Element, Event>
  ) => void;
  taskChoiceValue: AutocompleteResult[];
  taskKindValue: AutocompleteResult[];
  onChangeTaskKind: (
    newValue: ElectionRecordResult[],
    reason: FetchAutocompleteChangeReason,
    event: React.SyntheticEvent<Element, Event>
  ) => void;
  noteValue: string;
  onChangeValue: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const TasksForm = ({
  electionValue = [] as ElectionRecordResult[],
  onChangeElection = () => {},
  isSinceSpecificElection = false,
  handleSpecificElectionChange = () => {},
  onChangeTaskChoice = () => {},
  taskChoiceValue = [] as AutocompleteResult[],
  taskKindValue = [] as AutocompleteResult[],
  onChangeTaskKind = () => {},
  noteValue = '',
  onChangeValue = () => {},
}: TasksFormProps) => {
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);

  const lookup = async (lookupValue: string) => {
    const token = await getAccessTokenSilently();
    const lists = (await getElectionsContaining(token, lookupValue, domainCode))
      .map((x) => {
        return {
          id: x.id,
          label: x.name,
        } as ElectionRecordResult;
      })
      .sort(labelSorting);

    return lists;
  };

  const excessiveElections = isSinceSpecificElection && electionValue.length > 1;

  const taskKindCodes = taskKindValue.length
    ? taskKindValue.map((x) => TASK_KIND_MAPPING_REVERSE[x.label])
    : [];
  const taskChoiceCodes = taskChoiceValue.length
    ? taskChoiceValue.map((x) => TASK_CHOICE_MAPPING_REVERSE[x.label])
    : [];

  const taskStatusError = !arePermissibleTaskChoicesAndKinds(
    taskChoiceCodes as TaskChoice[],
    taskKindCodes as TaskKind[]
  );
  const taskStatusHelperText = taskStatusError ? 'Task status invalid for selected task type' : '';

  return (
    <>
      <AccordionField title="Election">
        <FormControlLabel
          sx={{ marginTop: -0.5 }}
          control={
            <Checkbox checked={isSinceSpecificElection} onChange={handleSpecificElectionChange} />
          }
          label="Since specific election"
        />
        <FetchAutocomplete
          lookup={lookup}
          minLength={3}
          label="Add an election"
          value={electionValue}
          onChange={onChangeElection}
          data-testid="Add an election"
          loadingText="Looking for elections..."
          noOptionsText="No elections found"
          popupIcon={<SearchIcon />}
          disableIconFlip
          error={excessiveElections}
          helperText={
            excessiveElections
              ? 'Please select a single election or uncheck the since specific election box'
              : ''
          }
        />
      </AccordionField>

      <AccordionField
        title={
          electionValue.length
            ? 'Task options for selected election(s)'
            : 'Select at least one election to see more task options'
        }
      />
      {electionValue.length !== 0 && (
        <>
          <AccordionField title={taskKindValue.length ? 'Task' : 'Task: Any'}>
            <FetchAutocomplete
              minLength={0}
              label="Add a task"
              preLoadedOptions={TASK_KIND_MAPPING_WITH_IDS}
              value={taskKindValue}
              onChange={onChangeTaskKind}
              data-testid="taskkinds"
              loadingText="Looking for tasks..."
              noOptionsText="No tasks found"
            />
          </AccordionField>
          <AccordionField title={taskChoiceValue.length ? 'Status' : 'Status: Any'}>
            <FetchAutocomplete
              minLength={0}
              label="Add a status"
              preLoadedOptions={TASK_CHOICE_MAPPING_WITH_IDS}
              value={taskChoiceValue}
              onChange={onChangeTaskChoice}
              data-testid="campaignstatuses"
              loadingText="Looking for statuses..."
              noOptionsText="No statuses found"
              error={taskStatusError}
              helperText={taskStatusHelperText}
            />
          </AccordionField>

          <AccordionField title="Task note (optional)">
            <TextField
              id="tasknotes"
              label={'Enter text to search for'}
              type="text"
              fullWidth
              variant="filled"
              value={noteValue}
              onChange={onChangeValue}
              slotProps={{
                htmlInput: {
                  maxLength: TASK_NOTE_MAX_LENGTH,
                },
              }}
            />
          </AccordionField>
        </>
      )}
    </>
  );
};

export default TasksForm;
